import { Button, Input, Spin, Select, notification } from 'antd';
import { useEffect, useState } from 'react';
import { Dynamics } from '@/services/dynamics/dynamics';
import { entityFields } from '@/components/CallPanel/config';
import { LoadingOutlined } from '@ant-design/icons';
import { EEntity } from '@/services/dynamics/enums/entity.enum';
import { SelectValueType } from '@/shared/types/FormSelect';
import {
  getFieldValidationConfigForObject,
  validateFormData,
  getErrorIfNotValid,
} from './FormValidationConfig';
import './Forms.scss';

const errorText = '* This value is required';
const antIcon = <LoadingOutlined spin />;
const FIELD_VALIDATION_CONFIG = getFieldValidationConfigForObject('Case');

interface IProps {
  setUnsavedFormData: (data: any) => void;
  data: any;
  prefilledPhone: string;
}

export const NewCaseForm = ({ setUnsavedFormData, data }: IProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [contactOptions, setContactOptions] = useState<SelectValueType[]>([]);
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [notificationApi, contextHolder] = notification.useNotification();
  const [formData, setFormData] = useState(
    data?.Case || {
      caseTitle: '',
      customer: '',
    },
  );

  const [formDataErrors, setFormDataErrors] = useState({
    caseTitle: '',
    customer: '',
  });

  const loadContacts = async () => {
    const data = await Dynamics.searchRelateToEntities(EEntity.CONTACT);
    await setContactOptions(data);
  };

  useEffect(() => {
    loadContacts();
  }, []);

  const onContactSelect = (val) => {
    setSelectedOption(val.value);
  };

  const changeValue = (item: string) => {
    return (e) => {
      setUnsavedFormData({
        Case: {
          ...formData,
          [item]: e.target.value,
        },
      });
      setFormData({
        ...formData,
        [item]: e.target.value,
      });
      setFormDataErrors({
        ...formDataErrors,
        [item]: getErrorIfNotValid(
          e.target.value,
          FIELD_VALIDATION_CONFIG[item],
        ),
      });
    };
  };

  const validate = () => {
    const errors = {
      caseTitle: '',
      customer: '',
    };
    let isOk = validateFormData(formData, errors, FIELD_VALIDATION_CONFIG);

    if (!selectedOption) {
      errors.customer = errorText;
      isOk = false;
    }

    setFormDataErrors(errors);
    return isOk;
  };

  const showRes = async () => {
    const isValid = validate();
    const data = {
      title: formData.caseTitle.trim(),
      'customerid_contact@odata.bind': `/contacts(${selectedOption})`,
    };
    if (isValid) {
      setIsLoading(true);
      await Dynamics.saveNewEntity('incident', data);
      setFormData({
        title: '',
        customer: '',
      });
      setIsLoading(false);
      setUnsavedFormData(null);
      setSelectedOption('');
      setTimeout(() => {
        notificationApi.success({
          message: 'Case successfully created',
          placement: 'bottomRight',
          duration: 3,
          className: 'notifications',
        });
      }, 0);
    }
  };

  return isLoading ? (
    <Spin className="spinner" indicator={antIcon} />
  ) : (
    <div>
      {contextHolder}
      <p className="heading">Fill in the form</p>
      <div className="form-container">
        {Object.keys(entityFields.case).map((item) => {
          return item === 'customer' ? (
            <div key={item} className="line fullw">
              {entityFields.case[item]}
              <Select
                showSearch
                labelInValue
                className="note__relate-options-customer"
                options={contactOptions}
                value={
                  contactOptions.find(
                    (item) => item.value === selectedOption,
                  ) || null
                }
                onSelect={onContactSelect}
                placeholder="Please choose a customer"
                filterOption={(inputValue, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(inputValue.toLowerCase())
                }
                status={formDataErrors[item] ? 'error' : ''}
              />
              {formDataErrors[item] ? (
                <div className="form-error">{formDataErrors[item]}</div>
              ) : (
                <></>
              )}
            </div>
          ) : (
            <div key={item} className="line">
              {entityFields.case[item]}
              <Input
                placeholder={entityFields.case[item]}
                value={formData[item]}
                onChange={changeValue(item)}
                status={formDataErrors[item] ? 'error' : ''}
              />
              {formDataErrors[item] ? (
                <div className="form-error">{formDataErrors[item]}</div>
              ) : (
                <></>
              )}
            </div>
          );
        })}
      </div>
      <div className="button-block">
        <Button onClick={showRes} data-testid="create-case">
          Submit
        </Button>
      </div>
    </div>
  );
};
